import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DatatableWrapper, TableBody, TableHeader } from "react-bs-datatable";
import ExportDialog from "./Export/ExportDialog";
import { Skeleton } from "./Skeleton/Skeleton.jsx";
import "./NewCustomDataTable.css";

// The flat list skin. It only applies inside a .td-list wrapper, so a
// page opts in and unconverted pages keep the card they had.
import './datalist.css';

const formatAuditDate = (value) => {
  if (!value) return "N/A";
  const d = value instanceof Date ? value : new Date(value);
  if (!d.getTime()) return "N/A";
  return d.toLocaleString();
};

/**
 * The page buttons to draw: first page, a window around the current one,
 * the last page, and an ellipsis wherever that skips a run. Returns page
 * numbers and the string "gap", so the caller stays declarative.
 *
 *   1 [2] 3 4 5 ... 323
 */
const pageWindow = (current, total, span = 2) => {
  if (total <= 1) return [1];
  const wanted = new Set([1, total]);
  for (let p = current - span; p <= current + span; p += 1) {
    if (p > 1 && p < total) wanted.add(p);
  }
  // A lone skipped page reads better as the page itself than as "...".
  const pages = [...wanted].sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => {
    if (i > 0) {
      const previous = pages[i - 1];
      if (p - previous === 2) out.push(previous + 1);
      else if (p - previous > 2) out.push("gap");
    }
    out.push(p);
  });
  return out;
};

/* Skeleton rows while a page of the list is on its way. Widths cycle by
   row and column so the placeholder reads as text of uneven length
   rather than a grid of identical bars. */
const SKELETON_WIDTHS = ["70", "55", "85", "40", "70", "55"];
const skeletonRowCount = (perPage) => Math.min(Math.max(Number(perPage) || 6, 3), 8);

const withAuditHeaders = (headers) => {
  const list = Array.isArray(headers) ? headers : [];
  const auditHeaders = [];
  if (auditHeaders.length === 0) return list;
  const actionIndex = list.findIndex((h) => h?.prop === "action");
  if (actionIndex === -1) return [...list, ...auditHeaders];
  return [
    ...list.slice(0, actionIndex),
    ...auditHeaders,
    ...list.slice(actionIndex),
  ];
};

const NewCustomDataTable = ({
  data,
  headers,
  totalRecords,
  currentPage,
  recordsPerPage,
  onPageChange,
  addButtonText = "Add",
  addButtonLink = "/add",
  pageTitle = "Data",
  searchTerm = "",
  selectedField = "all",
  addButton = true,
  exportButton = true,
  exportLabel = "Export",
  // It opens the export dialog now; the file and its format are chosen
  // there. "Export data as CSV" was the tooltip when the click was the
  // whole interaction.
  exportTooltip = "Choose what to export",
  exportDisabled = false,
  exportLoading = false,
  onExport,
  onSearchChange = () => {},
  onSelectedFieldChange = () => {},
  serverSide = false,
  onRowsPerPageChange = () => {},
  searchButton = false,
  loading = false,
  // True while a scroll batch is on its way. The rows already on screen
  // stay where they are and the new ones arrive underneath, so the table
  // must not be veiled for it -- see the overlay below.
  loadingMore = false,
  showLastButton = true,
  searchableFields,
  // Opt-out, not opt-in: 27 pages render this toolbar and must keep it.
  // A page that builds its own search and filter section passes false so
  // the two do not appear one above the other.
  showToolbar = true,
  // A page that loads by scrolling has no page buttons to draw.
  showPagination = true,
  // The whole footer -- rows select and page buttons. A page that puts
  // the rows control in its own filter section turns this off.
  showFooter = true,
  // The batch sizes offered. Default is what every existing page shows.
  rowsPerPageOptions = [5, 10, 20],
  // Fired when a row is clicked. Left undefined by default, so the rows
  // on every other page stay inert. react-bs-datatable only fires this
  // for TR and TD targets, so a button inside a cell still handles its
  // own click without the row also reacting.
  onRowClick,
  // Props spread onto each `tr` -- used to mark clickable rows up for
  // the keyboard when onRowClick is set.
  rowProps,
  // Which elements inside a row count as a click on the row.
  // react-bs-datatable defaults to ['TR', 'TD'], so a click landing on
  // the <span> a cell wraps its text in does nothing -- only the cell
  // padding around the text works. Pass "*" when the whole row is the
  // control and nothing inside it handles its own clicks.
  validRowClickTagNames,
}) => {
  const navigate = useNavigate();
  const [localRowsPerPage, setLocalRowsPerPage] = useState(recordsPerPage);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [localSelectedField, setLocalSelectedField] = useState(selectedField);
  const [hasError, setHasError] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => { setLocalSearchTerm(searchTerm); }, [searchTerm]);
  useEffect(() => { setLocalSelectedField(selectedField); }, [selectedField]);

  useEffect(() => {
    if (!loading) {
      const dataArray = Array.isArray(data) ? data : data?.data || [];
      if (dataArray.length === 0 && localSearchTerm) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    }
  }, [loading, data, localSearchTerm]);

  const effectiveHeaders = withAuditHeaders(headers);

  const searchableFieldsToUse = searchableFields
    ? searchableFields
    : effectiveHeaders
        .filter(
          (header) =>
            header.prop !== "action" &&
            header.prop !== "createdAt" &&
            header.prop !== "updatedAt",
        )
        .map((header) => ({ title: header.title, prop: header.prop }));

  const dataArray = Array.isArray(data) ? data : data?.data || [];
  // A fresh load only -- a scroll batch appends under rows already being
  // read, and the foot under the list carries its spinner.
  const showSkeleton = loading && !loadingMore;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const pageButtons = pageWindow(currentPage, totalPages || 1);

  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setLocalRowsPerPage(newRowsPerPage);
    onRowsPerPageChange(newRowsPerPage);
    onPageChange(1);
  };

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return "";
    const normalized = path.replace(/\[(\d+)\]/g, ".$1");
    const parts = normalized.split(".");
    return parts.reduce((curr, part) => (curr ? curr[part] : ""), obj);
  };

  const extractTextFromCell = (cellResult) => {
    if (cellResult == null) return "";
    if (typeof cellResult === "string") return cellResult;
    if (typeof cellResult === "number" || typeof cellResult === "boolean")
      return String(cellResult);
    if (React.isValidElement(cellResult)) {
      const children = cellResult.props?.children;
      if (children == null) return "";
      if (typeof children === "string") return children;
      if (typeof children === "number" || typeof children === "boolean")
        return String(children);
      if (Array.isArray(children)) {
        return children
          .map((child) => extractTextFromCell(child))
          .filter((text) => text !== "")
          .join(" ");
      }
      if (React.isValidElement(children)) {
        return extractTextFromCell(children);
      }
      return String(cellResult);
    }
    try {
      return JSON.stringify(cellResult);
    } catch {
      return "";
    }
  };

  const getValueForHeader = (row, header) => {
    let value = "";
    if (header.cell) {
      const rendered = header.cell(row);
      value = extractTextFromCell(rendered);
    } else {
      const raw = getNestedValue(row, header.prop);
      if (raw == null) return "";
      if (Array.isArray(raw)) {
        value = raw
          .map((item) =>
            typeof item === "object" ? item.name || JSON.stringify(item) : item,
          )
          .join("; ");
      } else if (typeof raw === "object") {
        value = raw.name || JSON.stringify(raw);
      } else {
        value = String(raw);
      }
    }
    if (typeof value === "string" && value.includes("}")) {
      const parts = value.split("}");
      const lastPart = parts[parts.length - 1].trim();
      if (lastPart) value = lastPart;
    }
    return value;
  };

  /*
   * The table's own columns, in the shape exportRows.js wants. The
   * action column is dropped -- it holds buttons, and a CSV cell
   * reading "EditDelete" is what the old export produced.
   *
   * getValueForHeader is what the CSV already used, so a cell rendered
   * by header.cell() exports the text it shows rather than the object
   * behind it. The escaping is no longer done here: it belongs to the
   * writer, and the two spellings of it disagreed about newlines.
   */
  const exportColumns = effectiveHeaders
    .filter((header) => header.prop !== "action")
    .map((header, i) => ({
      // react-bs-datatable wants a prop on every header, but a column
      // built entirely from `cell` can be written without one, and two
      // columns keyed `undefined` would tick and untick together.
      key: header.prop || `col-${i}`,
      label: header.title,
      value: (row) => getValueForHeader(row, header),
    }));

  /*
   * What the dialog says the rows ARE. A server-paged list holds only
   * the page it fetched, and the old button exported those silently
   * under a name that claimed to be the whole list.
   */
  const exportScopeNote = (() => {
    const parts = [];
    if (serverSide && totalRecords > dataArray.length) {
      parts.push(
        `Page ${currentPage} of ${totalPages || 1} — the other ${(
          totalRecords - dataArray.length
        ).toLocaleString()} rows are on the server and are not in this file.`,
      );
    } else if (totalRecords && totalRecords !== dataArray.length) {
      parts.push(`${dataArray.length} of ${totalRecords} rows.`);
    }
    if (localSearchTerm) {
      const field =
        localSelectedField === "all"
          ? "all fields"
          : searchableFieldsToUse.find(
              (f) => (f.prop || f.value) === localSelectedField,
            )?.title || localSelectedField;
      parts.push(`Filtered by “${localSearchTerm}” in ${field}.`);
    }
    return parts.join(" ") || undefined;
  })();

  const handleExportClick = () => {
    // A page that builds its own export -- a summary with computed
    // columns, a roster the table does not hold -- still wins.
    if (typeof onExport === "function") return onExport();
    return setExportOpen(true);
  };

  const handleSearchClick = () => {
    setHasError(false);
    onSearchChange(localSearchTerm, localSelectedField);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
    setHasError(false);
    onSearchChange("", localSelectedField);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    setHasError(false);
    if (!searchButton) onSearchChange(value, localSelectedField);
  };

  const handleFieldChange = (e) => {
    const value = e.target.value;
    setLocalSelectedField(value);
    if (!searchButton) {
      onSearchChange(localSearchTerm, value);
    } else {
      onSelectedFieldChange(value);
    }
  };

  const handleKeyPress = (e) => {
    if (searchButton && e.key === "Enter") handleSearchClick();
  };

  const handleRefreshClick = () => {
    if (hasError) return;
    setLocalSearchTerm("");
    setHasError(false);
    onSearchChange("", localSelectedField);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const trimmed = e.clipboardData.getData("text").trim();
    setLocalSearchTerm(trimmed);
    setHasError(false);
    if (!searchButton) onSearchChange(trimmed, localSelectedField);
  };

  return (
    <div className="dt-card mt-3">
      <div className="dt-card-body">

        {/* ── Toolbar ── */}
        {showToolbar && (
        <div className="dt-toolbar">
          <div className="dt-toolbar-search">
            <div className="dt-search-input-wrap">
              <input
                type="text"
                placeholder="Search..."
                value={localSearchTerm}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onPaste={handlePaste}
                className="dt-search-input"
                style={{ paddingRight: localSearchTerm && searchButton ? "80px" : undefined }}
              />
              {localSearchTerm && searchButton && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="dt-clear-btn"
                  aria-label="Clear search"
                >
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>

            <select
              value={localSelectedField}
              onChange={handleFieldChange}
              className="dt-field-select"
            >
              <option value="all">All Fields</option>
              {searchableFieldsToUse.map((field) => (
                <option key={field.prop || field.value} value={field.prop || field.value}>
                  {field.title || field.label}
                </option>
              ))}
            </select>

            {searchButton && (
              <button
                type="button"
                onClick={handleRefreshClick}
                disabled={hasError}
                className="dt-icon-btn"
                title={hasError ? "Cannot refresh — last search failed" : "Refresh data"}
                aria-label="Refresh data"
                style={hasError ? { pointerEvents: "none", opacity: 0.5 } : {}}
              >
                <i className="fa fa-refresh"></i>
              </button>
            )}

            {searchButton && (
              <button
                type="button"
                onClick={handleSearchClick}
                disabled={!localSearchTerm}
                className="dt-search-btn"
              >
                <i className="fa fa-search me-1"></i>
                <span className="dt-btn-label">Search</span>
              </button>
            )}
          </div>

          <div className="dt-toolbar-actions">
            {exportButton && (
              <button
                type="button"
                onClick={handleExportClick}
                disabled={exportDisabled || exportLoading}
                className="dt-icon-btn"
                title={exportTooltip}
                /* It opens a dialog rather than downloading on the
                   click, and a button that opens one has to say so.
                   Not claimed when the page supplied its own handler:
                   that one may well download straight away. */
                aria-haspopup={onExport ? undefined : "dialog"}
                aria-expanded={onExport ? undefined : exportOpen}
              >
                <i className="fa fa-download me-1"></i>
                <span className="dt-btn-label">{exportLabel}</span>
              </button>
            )}

            {addButton && (
              <button
                type="button"
                onClick={() => navigate(addButtonLink)}
                className="dt-add-btn"
              >
                <i className="fa fa-plus me-1"></i>
                <span className="dt-btn-label">{addButtonText}</span>
              </button>
            )}
          </div>
        </div>
        )}

        {/* ── Table ── */}
        <DatatableWrapper
          // The page number is in the key so a paged table re-inits when
          // the page turns. A scrolling list appends to the same body, so
          // keeping it there would unmount and rebuild the whole table on
          // every batch -- the list visibly jumped each time.
          key={showPagination ? `${recordsPerPage}-${currentPage}` : `${recordsPerPage}`}
          body={dataArray}
          headers={effectiveHeaders}
          paginationOptionsProps={{
            initialState: {
              rowsPerPage: serverSide ? 9999 : recordsPerPage,
              options: [5, 10, 20],
            },
          }}
        >
          <div className="dt-table-wrap" style={{ position: "relative", minHeight: "120px" }}>
            {/* A fresh load draws skeleton rows under the real header, in
                the table's own cells, so the list skin, the column widths
                and the phone layout are the ones the data will use and
                nothing moves when it lands. A scroll batch never does: it
                appends under rows already being read, and the foot under
                the list carries the spinner for it. */}
            {showSkeleton && (
              <span className="lms-skel-sr" role="status">Loading {pageTitle}</span>
            )}
            <div className="table-responsive">
              <table className="table dt-table" aria-busy={showSkeleton || undefined}>
                <TableHeader />
                {showSkeleton ? (
                  <tbody className="dt-skel-body" aria-hidden="true">
                    {Array.from({ length: skeletonRowCount(recordsPerPage) }, (_, r) => (
                      <tr key={r}>
                        {effectiveHeaders.map((header, c) => (
                          <td key={header.prop || c}>
                            {header.prop === "action" ? (
                              <Skeleton shape="pill" />
                            ) : (
                              <Skeleton width={SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length]} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <TableBody
                    onRowClick={onRowClick}
                    rowProps={rowProps}
                    validRowClickTagNames={validRowClickTagNames}
                  />
                )}
              </table>
            </div>
          </div>

          {/* ── Footer ── */}
          {showFooter && (
          <div className="dt-footer">
            <div className="dt-rows-per-page">
              <span className="dt-rpp-label">Rows</span>
              <select
                value={serverSide ? recordsPerPage : localRowsPerPage}
                onChange={handleRowsPerPageChange}
                className="dt-rpp-select"
              >
                {rowsPerPageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {showPagination && (
            <div className="dt-pagination">
              <button
                className="dt-page-btn dt-page-btn--step"
                disabled={!hasPrevious}
                onClick={() => onPageChange(currentPage - 1)}
              >
                Prev
              </button>

              {pageButtons.map((p, i) =>
                p === "gap" ? (
                  <span key={`gap-${i}`} className="dt-page-gap">&hellip;</span>
                ) : (
                  <button
                    key={p}
                    className={`dt-page-btn${p === currentPage ? " dt-page-btn--on" : ""}`}
                    aria-current={p === currentPage ? "page" : undefined}
                    onClick={() => onPageChange(p)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="dt-page-btn dt-page-btn--step"
                disabled={!hasNext}
                onClick={() => onPageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
            )}
          </div>
          )}
        </DatatableWrapper>

      </div>

      {/* Portalled to <body> from inside, so it does not matter that it
          is written at the bottom of a card with overflow: hidden. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={pageTitle}
        columns={exportColumns}
        rows={dataArray}
        scopeNote={exportScopeNote}
        filenameBase={pageTitle}
      />
    </div>
  );
};

export default NewCustomDataTable;
